/**
 * use-save-pipeline.ts — Phase 051 Plan 01 Task 2.
 *
 * React seam wiring the pure `runSavePipeline` controller to the 4
 * Zustand stores + 3 toast effects. Controller stays React-free (AP9).
 *
 * SC#4 single-flight (D-051-02): MODULE-LEVEL `_saveInFlight` synchronous
 * gate (Codex review fix #2). A prior design that used a hook-local boolean
 * ref RESET on SaveButton remount, defeating the gate when a re-render
 * unmounted and re-mounted the button while a commit was in-flight. The
 * module-level flag + listener Set survives any number of remounts; one
 * global Save pipeline at a time, app-wide. `_saveListeners` push the
 * in-flight value into every mounted SaveButton's local `useState` so the
 * `disabled` attribute re-renders consistently across all instances.
 *
 * T-051-01 mitigation (Elevation of Privilege): `tenantId` is read ONLY
 * via `deriveTenantId(useStudioStore.getState().session)` (auth-slice.ts —
 * derives from JWT user.id). NEVER from untrusted browser URL. The hook
 * never reads URL search params, never reads window location, and never
 * calls the abox deeplink parser. See the grep-guard tests in
 * save-button.test.tsx Test 8.
 *
 * AP8 — the module-scope flag is local to THIS file, NOT a subscription
 * to `useMutationStore.status` (which is global across all 4 mutation
 * tools and would false-disable Save during unrelated background calls).
 */

import { useEffect, useState, useCallback } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { useDraftStore } from '@/features/preview/store/draft-store'
import { useMutationStore } from '@/features/design-system/ontology/mutation-store'
import { useToastStore } from '@/store/toast-store'
import { deriveTenantId } from '@/store/auth-slice'
import { showShaclErrorToast } from '@/features/design-system/ontology/shacl-error-toast'
import { runSavePipeline } from './save-controller'

export interface UseSavePipeline {
  run: () => Promise<void>
  isInFlight: boolean
  dirtyCount: number
}

// Codex 2026-05-23 review fix #2 — module-level single-flight state +
// listener Set. The flag + listeners survive any SaveButton remount,
// keyboard shortcut, or programmatic re-invocation. `_setSaveInFlight`
// notifies every mounted hook instance so all SaveButton instances
// re-render in lock-step.
let _saveInFlight = false
const _saveListeners = new Set<(v: boolean) => void>()
function _setSaveInFlight(v: boolean): void {
  _saveInFlight = v
  _saveListeners.forEach((fn) => fn(v))
}

export function useSavePipeline(): UseSavePipeline {
  const [isInFlight, setIsInFlight] = useState(_saveInFlight)
  const dirtyCount = useDraftStore((s) => s.dirtyCount())

  useEffect(() => {
    _saveListeners.add(setIsInFlight)
    // On (re)mount, re-sync to the current module value — defends against
    // updates the listener missed while unmounted.
    setIsInFlight(_saveInFlight)
    return () => {
      _saveListeners.delete(setIsInFlight)
    }
  }, [])

  const run = useCallback(async () => {
    // SC#4 synchronous gate — short-circuit BEFORE any state update can
    // race. Survives remount because the flag is at module scope.
    if (_saveInFlight) return
    _setSaveInFlight(true)
    try {
      const studio = useStudioStore.getState()
      const tenantId = deriveTenantId(studio.session)
      const appId = studio.currentProjectId
      const currentProject = studio.currentProject
      const drafts = useDraftStore.getState().drafts
      const dirtyIds = useDraftStore.getState().dirtyBlockIds()
      if (!tenantId || !appId || !currentProject || dirtyIds.size === 0) {
        // Short-circuit — matches mutation-client-init's null-tenant gate.
        return
      }
      await runSavePipeline({
        tenantId,
        appId,
        currentProject,
        drafts,
        dirtyBlockIds: dirtyIds,
        addBlock: (args) => useMutationStore.getState().addBlock(args),
        clearBlockDraft: (blockId) =>
          useDraftStore.getState().clearBlock(blockId),
        commitBlockBaseline: (blockId, properties) =>
          useStudioStore.getState().commitBlockBaseline(blockId, properties),
        // Phase 051.1 D-051.1-04 (Codex P2 fix) — mid-flight draft
        // snapshot read. Controller calls this BEFORE addBlock + AGAIN
        // after the await resolves to detect mid-flight property-panel
        // edits and preserve them.
        readBlockDraft: (blockId) =>
          useDraftStore.getState().readBlockDraft(blockId),
        showShaclErrorToast,
        showGenericErrorToast: (err) =>
          useToastStore.getState().addToast({
            type: 'error',
            message: `Save failed: ${err.message}`,
          }),
        showSuccessToast: (count) =>
          useToastStore.getState().addToast({
            type: 'success',
            message: count === 1 ? '1 block saved' : `${count} blocks saved`,
          }),
      })
    } finally {
      _setSaveInFlight(false)
    }
  }, [])

  return { run, isInFlight, dirtyCount }
}

/**
 * Test seam — DO NOT USE IN PRODUCTION. Resets the module-level flag for
 * vitest isolation between cases. Imported by save-button.test.tsx
 * `beforeEach` hook + the module-level gate test.
 */
export function __resetSaveInFlightForTest(): void {
  _saveInFlight = false
  _saveListeners.clear()
}
